/**
 * Fish counted per day.
 *
 * Its own file rather than sitting with the lice specs: this chart is about how many fish a
 * day's averages rest on, not about lice, and it shares nothing with them but the row shape.
 */

import { dayAxis } from '@/components/charts/generic/dayAxis';
import type { MetricChartSpec } from '@/components/charts/generic/types';
import { graphSettings } from '@/config/settings';
import type { Locale } from '@/utils/localeFormatters';

import type { LiceChartDay } from './liceChartMetrics';

export interface FishCountChartSpecParams {
    days: LiceChartDay[];
    /** Already translated. */
    label: string;
    axisLabel: string;
    locale: Locale;
}

/** Above this many fish on the busiest day, the axis steps in 500s rather than 100s. */
const FISH_COUNT_COARSE_STEP_THRESHOLD = 1000;
const FISH_COUNT_COARSE_STEP = 500;
const FISH_COUNT_FINE_STEP = 100;

/**
 * Fish counted per day.
 *
 * The step size adapts to the busiest day, which is what the legacy chart did with a ternary
 * inlined in the page. It is a property of the data, so it belongs with the spec.
 */
export function buildFishCountChartSpec(params: FishCountChartSpecParams): MetricChartSpec<'bar'> {
    const { days, label, axisLabel, locale } = params;

    const busiestDay = days.reduce((most, day) => Math.max(most, day.count), 0);

    return {
        kind: 'bar',
        days: dayAxis(days, (day) => day.day),
        series: [
            {
                key: 'fishCount',
                label,
                color: graphSettings.graphColors.fishCount,
                data: days.map((day) => ({ day: day.day, value: day.count })),
            },
        ],
        left: {
            label: axisLabel,
            beginAtZero: true,
            stepSize:
                busiestDay > FISH_COUNT_COARSE_STEP_THRESHOLD
                    ? FISH_COUNT_COARSE_STEP
                    : FISH_COUNT_FINE_STEP,
            tickDecimals: 0,
            valueDecimals: 0,
        },
        locale,
    };
}
